import { Link, Redirect } from 'expo-router';
import {
  Bell,
  Car,
  CheckCircle2,
  ChevronRight,
  Heart,
  MessageCircle,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Timeline,
  UserPlus,
  Users,
} from 'lucide-react-native';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import { BrandLogo } from '@/src/components/BrandLogo';
import { Button } from '@/src/components/Button';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { SessionLoadingView } from '@/src/components/SessionLoadingView';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  fetchPublishedChangeLogEntries,
  type ChangeLogEntry,
} from '@/src/features/changelog/changelog-service';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';

type LucideIcon = ComponentType<{
  color?: string;
  size?: number;
  strokeWidth?: number;
}>;

type Feature = {
  description: string;
  Icon: LucideIcon;
  title: string;
};

const features: Feature[] = [
  {
    description:
      'Share public updates or keep posts for friends, with simple controls for who can see each moment.',
    Icon: MessageCircle,
    title: 'Fresh posts',
  },
  {
    description:
      'Build a following list that feels personal, then use Frienzzone to keep up with people you chose.',
    Icon: Users,
    title: 'Real friends',
  },
  {
    description:
      'React with more than a like, comment with media, and keep conversations around the original post.',
    Icon: Heart,
    title: 'Better reactions',
  },
  {
    description:
      "Show your cars, browse friends' vehicles, and share garage listings from inside Frienzzi.",
    Icon: Car,
    title: 'Garage profiles',
  },
  {
    description:
      'Invite friends with your own link and see accepted invites on your profile when they join.',
    Icon: UserPlus,
    title: 'Invite links',
  },
  {
    description:
      'Install Frienzzi to your phone home screen and choose the notifications that matter to you.',
    Icon: Bell,
    title: 'App-like install',
  },
];

const differences = [
  'Plain-language explanations for meaningful product and algorithm changes.',
  'A friend-first feed with public and friends-only sharing controls.',
  'Notification settings for in-app, email, and push instead of one all-or-nothing switch.',
  'Invite links that show who helped bring real people into the community.',
] as const;

function FeatureCard({ description, Icon, title }: Feature) {
  return (
    <View style={styles.featureCard}>
      <View style={styles.featureIconWrap}>
        <Icon color={colors.primary} size={24} strokeWidth={2.5} />
      </View>
      <Text style={styles.featureTitle}>{title}</Text>
      <Text style={styles.featureDescription}>{description}</Text>
    </View>
  );
}

function LatestTransparencyCard() {
  const [entry, setEntry] = useState<ChangeLogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    fetchPublishedChangeLogEntries(1)
      .then((entries) => {
        if (!active) {
          return;
        }

        setEntry(entries[0] ?? null);
        setError(false);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setError(true);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <View style={styles.latestCard}>
      <View style={styles.latestHeader}>
        <View style={styles.latestIconWrap}>
          <Timeline color={colors.primary} size={24} strokeWidth={2.6} />
        </View>
        <View style={styles.latestHeaderCopy}>
          <Text style={styles.eyebrow}>Latest transparency update</Text>
          <Text style={styles.latestHint}>Pulled from the public changelog</Text>
        </View>
      </View>
      {loading ? (
        <View style={styles.latestStatus}>
          <ActivityIndicator color={colors.primary} size="small" />
          <Text style={styles.latestStatusText}>Loading latest update...</Text>
        </View>
      ) : entry ? (
        <View style={styles.latestEntry}>
          <Text style={styles.latestDate}>{formatDate(entry.publishedAt)}</Text>
          <Text style={styles.latestTitle}>{entry.title}</Text>
          <Text style={styles.latestSummary}>{entry.summary}</Text>
        </View>
      ) : (
        <Text style={styles.latestStatusText}>
          {error
            ? 'The latest update could not load right now.'
            : 'Published updates will appear here as Frienzzi changes.'}
        </Text>
      )}
      <Link href="/changelog" asChild>
        <Pressable style={styles.latestLink}>
          <Text style={styles.latestLinkText}>View all updates</Text>
          <ChevronRight color={colors.secondary} size={18} strokeWidth={2.7} />
        </Pressable>
      </Link>
    </View>
  );
}

function HomePreview({ compact }: { compact: boolean }) {
  return (
    <View style={[styles.previewPanel, compact ? styles.previewPanelCompact : null]}>
      <View style={styles.previewHeader}>
        <BrandLogo variant="icon" size="md" />
        <View style={styles.previewHeaderText}>
          <Text style={styles.previewName}>Frienzzi preview</Text>
          <Text style={styles.previewMeta}>Public feed + Frienzzone</Text>
        </View>
      </View>
      <View
        style={[
          styles.previewPostCard,
          compact ? styles.previewPostCardCompact : null,
        ]}
      >
        <View style={styles.previewPostHeader}>
          <View style={styles.previewAvatar}>
            <BrandLogo variant="icon" size="sm" />
          </View>
          <View style={styles.previewPostIdentity}>
            <Text style={styles.previewAuthor}>Your real friends</Text>
            <View style={styles.previewVisibilityPill}>
              <Users color={colors.secondary} size={13} strokeWidth={2.5} />
              <Text style={styles.previewVisibilityText}>Friends</Text>
            </View>
          </View>
        </View>
        <Text style={styles.previewPostText}>
          Fresh posts, honest reactions, and comments that stay connected to the
          moment.
        </Text>
        <View style={styles.previewMedia}>
          <Sparkles color={colors.surface} size={34} strokeWidth={2.6} />
        </View>
      </View>
      <View style={styles.previewActions}>
        <View style={styles.previewActionGroup}>
          <Heart color={colors.secondary} size={22} strokeWidth={2.6} />
          <Text style={styles.previewActionText}>Reactions</Text>
        </View>
        <View style={styles.previewActionGroup}>
          <MessageCircle color={colors.secondary} size={22} strokeWidth={2.6} />
          <Text style={styles.previewActionText}>Comments</Text>
        </View>
        <View style={styles.previewActionGroup}>
          <Share2 color={colors.secondary} size={22} strokeWidth={2.6} />
          <Text style={styles.previewActionText}>Share</Text>
        </View>
      </View>
    </View>
  );
}

export default function IndexRoute() {
  const { authInitTimedOut, initializing, user } = useAuth();
  const { width } = useWindowDimensions();
  const compact = width < 760;
  const sectionTitleStyle = [
    styles.sectionTitle,
    compact ? styles.sectionTitleCompact : null,
  ];

  if (initializing || authInitTimedOut) {
    return <SessionLoadingView />;
  }

  if (user) {
    return <Redirect href="/home" />;
  }

  return (
    <PublicPageShell maxWidth={1120} showFooterAddress={false}>
      <View style={[styles.hero, compact ? styles.heroCompact : null]}>
        <View style={[styles.heroScrim, compact ? styles.heroScrimCompact : null]}>
          <View style={styles.heroBrandRow}>
            <BrandLogo variant="icon" size={compact ? 'md' : 'lg'} />
            <Text
              style={[
                styles.heroBrandText,
                compact ? styles.heroBrandTextCompact : null,
              ]}
            >
              frienzzi
            </Text>
          </View>
          <Text
            style={[styles.heroTitle, compact ? styles.heroTitleCompact : null]}
          >
            Frienzzi
          </Text>
          <Text
            style={[
              styles.heroSubtitle,
              compact ? styles.heroSubtitleCompact : null,
            ]}
          >
            A more positive social network for real friends, fresh posts, honest
            reactions, and transparent product changes.
          </Text>
          <Text
            style={[
              styles.heroIncentive,
              compact ? styles.heroIncentiveCompact : null,
            ]}
          >
            Create your profile, invite your friends, and build your
            Frienzzone.
          </Text>
          <View
            style={[styles.heroActions, compact ? styles.stackedActions : null]}
          >
            <Link href="/sign-up" asChild>
              <Button
                title="Create account"
                style={compact ? styles.heroButtonFull : styles.heroButton}
              />
            </Link>
            <Link href="/sign-in" asChild>
              <Button
                title="Sign in"
                variant="secondary"
                style={compact ? styles.heroButtonFull : styles.heroButton}
              />
            </Link>
          </View>
        </View>
      </View>

      <View style={[styles.introBand, compact ? styles.stackBand : null]}>
        <View style={[styles.introCopy, compact ? styles.flexPanelCompact : null]}>
          <Text style={styles.eyebrow}>Built for actual connection</Text>
          <Text style={sectionTitleStyle}>
            Share with the people you care about, without losing the fun of a
            public feed.
          </Text>
          <Text style={styles.sectionBody}>
            Frienzzi gives you a familiar social feed, more expressive reactions,
            profile stats, invite tracking, a public changelog for meaningful
            changes, and a PWA install flow that works from the browser.
          </Text>
        </View>
        <HomePreview compact={compact} />
      </View>

      <View style={styles.differenceSection}>
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Why Frienzzi feels different</Text>
          <Text style={sectionTitleStyle}>
            Social tools should be understandable, personal, and useful.
          </Text>
        </View>
        <View style={styles.differenceList}>
          {differences.map((difference) => (
            <View key={difference} style={styles.differenceRow}>
              <CheckCircle2 color={colors.success} size={22} strokeWidth={2.6} />
              <Text style={styles.differenceText}>{difference}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.featureSection}>
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>What you can do</Text>
          <Text style={sectionTitleStyle}>Everything starts with people.</Text>
        </View>
        <View style={styles.featureGrid}>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </View>
      </View>

      <View style={styles.trustBand}>
        <View style={styles.trustItem}>
          <ShieldCheck color={colors.success} size={26} strokeWidth={2.5} />
          <Text style={styles.trustText}>Plain-language public changelog</Text>
        </View>
        <View style={styles.trustItem}>
          <Users color={colors.accent} size={26} strokeWidth={2.5} />
          <Text style={styles.trustText}>
            Follower, following, invite, and reaction stats
          </Text>
        </View>
        <View style={styles.trustItem}>
          <Bell color={colors.primary} size={26} strokeWidth={2.5} />
          <Text style={styles.trustText}>
            Notification controls for app, email, and push
          </Text>
        </View>
      </View>

      <View
        style={[
          styles.transparencyBand,
          compact ? styles.calloutBandCompact : null,
        ]}
      >
        <View style={styles.transparencyIcon}>
          <Timeline color={colors.surface} size={30} strokeWidth={2.7} />
        </View>
        <View
          style={[
            styles.transparencyCopy,
            compact ? styles.flexPanelCompact : null,
          ]}
        >
          <Text style={styles.eyebrow}>Transparency is part of the product</Text>
          <Text
            style={[
              styles.transparencyTitle,
              compact ? styles.calloutTitleCompact : null,
            ]}
          >
            When Frienzzi changes something important, users should be able to
            see what changed and why.
          </Text>
          <Text style={styles.transparencyBody}>
            Product updates that affect functionality, privacy, notifications,
            safety, ranking, feed behavior, recommendations, or algorithms are
            published in plain language so people are not left guessing.
          </Text>
        </View>
        <Link href="/changelog" asChild>
          <Button
            title="View changelog"
            variant="secondary"
            style={compact ? styles.transparencyButtonFull : styles.transparencyButton}
          />
        </Link>
      </View>

      <LatestTransparencyCard />

      <View style={[styles.installCta, compact ? styles.calloutBandCompact : null]}>
        <View style={styles.installIcon}>
          <Smartphone color={colors.surface} size={30} strokeWidth={2.6} />
        </View>
        <View style={[styles.installCopy, compact ? styles.flexPanelCompact : null]}>
          <Text style={styles.eyebrow}>Use it like an app</Text>
          <Text
            style={[
              styles.installTitle,
              compact ? styles.calloutTitleCompact : null,
            ]}
          >
            Install Frienzzi on your phone from the browser.
          </Text>
          <Text style={styles.installBody}>
            Add Frienzzi to your Home Screen for a faster app-style launch and
            optional push notifications when you choose to enable them.
          </Text>
        </View>
        <Link href="/install" asChild>
          <Button
            title="Install guide"
            variant="secondary"
            style={compact ? styles.installButtonFull : styles.installButton}
          />
        </Link>
      </View>

      <View style={[styles.finalCta, compact ? styles.finalCtaCompact : null]}>
        <Text style={styles.founderLine}>
          Frienzzi is built around a simple idea: social media should bring
          people closer, not reward negativity.
        </Text>
        <Text style={[styles.finalTitle, compact ? styles.finalTitleCompact : null]}>
          Ready to join Frienzzi?
        </Text>
        <Text style={styles.finalBody}>
          Create your profile, invite your friends, and start building a feed
          that feels like your people again.
        </Text>
        <View style={[styles.finalActions, compact ? styles.stackedActions : null]}>
          <Link href="/sign-up" asChild>
            <Button
              title="Create account"
              style={compact ? styles.finalButtonFull : styles.finalButton}
            />
          </Link>
          <Link href="/mission" asChild>
            <Pressable style={styles.textLinkButton}>
              <Text style={styles.textLinkButtonText}>Read the mission</Text>
            </Pressable>
          </Link>
        </View>
      </View>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  eyebrow: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
    letterSpacing: 0,
    lineHeight: 18,
    textTransform: 'uppercase',
  },
  differenceList: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.xl,
  },
  differenceRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  differenceSection: {
    gap: spacing.xl,
  },
  differenceText: {
    color: colors.text,
    flex: 1,
    fontSize: 17,
    fontWeight: '800',
    lineHeight: 24,
  },
  featureCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexBasis: 300,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.md,
    maxWidth: '100%',
    minWidth: 0,
    padding: spacing.xl,
  },
  featureDescription: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  featureGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  featureIconWrap: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  featureSection: {
    gap: spacing.xl,
  },
  featureTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
  },
  finalActions: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    justifyContent: 'center',
  },
  finalBody: {
    color: colors.mutedText,
    fontSize: 18,
    lineHeight: 27,
    maxWidth: 680,
    textAlign: 'center',
  },
  finalButton: {
    minWidth: 190,
  },
  finalButtonFull: {
    minWidth: 190,
    width: '100%',
  },
  finalCta: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.xxl,
  },
  finalCtaCompact: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  finalTitle: {
    color: colors.text,
    fontSize: 32,
    fontWeight: '900',
    lineHeight: 38,
    textAlign: 'center',
  },
  finalTitleCompact: {
    fontSize: 27,
    lineHeight: 33,
  },
  flexPanelCompact: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    maxWidth: '100%',
    width: '100%',
  },
  founderLine: {
    color: colors.secondary,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 27,
    maxWidth: 760,
    textAlign: 'center',
  },
  hero: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    minHeight: 560,
    overflow: 'hidden',
  },
  heroCompact: {
    minHeight: 0,
  },
  heroActions: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    justifyContent: 'center',
    marginTop: spacing.md,
  },
  heroBrandRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'center',
  },
  heroBrandText: {
    color: colors.text,
    fontSize: 34,
    fontWeight: '900',
    lineHeight: 40,
  },
  heroBrandTextCompact: {
    fontSize: 28,
    lineHeight: 34,
  },
  heroButton: {
    minWidth: 180,
  },
  heroButtonFull: {
    minWidth: 180,
    width: '100%',
  },
  heroIncentive: {
    color: colors.secondary,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 26,
    maxWidth: 620,
    textAlign: 'center',
  },
  heroIncentiveCompact: {
    fontSize: 16,
    lineHeight: 23,
    maxWidth: '100%',
    width: '100%',
  },
  heroScrim: {
    alignItems: 'center',
    flex: 1,
    gap: spacing.lg,
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
    paddingVertical: 72,
  },
  heroScrimCompact: {
    gap: spacing.md,
    maxWidth: '100%',
    minWidth: 0,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    width: '100%',
  },
  heroSubtitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '700',
    lineHeight: 32,
    maxWidth: 760,
    textAlign: 'center',
  },
  heroSubtitleCompact: {
    fontSize: 17,
    lineHeight: 25,
    maxWidth: '100%',
    width: '100%',
  },
  heroTitle: {
    color: colors.text,
    fontSize: 74,
    fontWeight: '900',
    lineHeight: 82,
    textAlign: 'center',
  },
  heroTitleCompact: {
    fontSize: 44,
    lineHeight: 50,
  },
  introBand: {
    alignItems: 'stretch',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xl,
  },
  introCopy: {
    flexBasis: 360,
    flexGrow: 1,
    gap: spacing.md,
    justifyContent: 'center',
  },
  installBody: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 24,
  },
  installButton: {
    minWidth: 160,
  },
  installButtonFull: {
    minWidth: 160,
    width: '100%',
  },
  installCopy: {
    flexBasis: 420,
    flexGrow: 1,
    gap: spacing.sm,
  },
  installCta: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xl,
    padding: spacing.xl,
  },
  installIcon: {
    alignItems: 'center',
    backgroundColor: colors.accent,
    borderRadius: 8,
    height: 58,
    justifyContent: 'center',
    width: 58,
  },
  installTitle: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '900',
    lineHeight: 31,
  },
  latestCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    padding: spacing.xl,
  },
  latestDate: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
    lineHeight: 17,
    textTransform: 'uppercase',
  },
  latestEntry: {
    gap: spacing.sm,
  },
  latestHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  latestHeaderCopy: {
    flex: 1,
    minWidth: 0,
  },
  latestHint: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  latestIconWrap: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  latestLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 36,
  },
  latestLinkText: {
    color: colors.secondary,
    fontSize: 15,
    fontWeight: '900',
  },
  latestStatus: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  latestStatusText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  latestSummary: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 24,
  },
  latestTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 28,
  },
  previewActionGroup: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  previewActions: {
    borderTopColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    justifyContent: 'space-between',
    paddingTop: spacing.lg,
  },
  previewActionText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
    lineHeight: 18,
  },
  previewAvatar: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 56,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 56,
  },
  previewAuthor: {
    color: colors.text,
    fontSize: 19,
    fontWeight: '900',
    lineHeight: 24,
  },
  previewHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  previewHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  previewMedia: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 170,
    justifyContent: 'center',
    marginTop: spacing.sm,
    width: '100%',
  },
  previewMeta: {
    color: colors.mutedText,
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 21,
  },
  previewName: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 28,
  },
  previewPanel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexBasis: 360,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.lg,
    maxWidth: '100%',
    minWidth: 0,
    padding: spacing.xl,
  },
  previewPanelCompact: {
    alignSelf: 'stretch',
    flexBasis: 'auto',
    padding: spacing.lg,
    width: 'auto',
  },
  previewPostCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  previewPostCardCompact: {
    padding: spacing.md,
  },
  previewPostHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  previewPostIdentity: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  previewPostText: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 23,
  },
  previewVisibilityPill: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  previewVisibilityText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  sectionBody: {
    color: colors.mutedText,
    fontSize: 17,
    lineHeight: 26,
    maxWidth: '100%',
  },
  sectionHeader: {
    gap: spacing.sm,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 34,
    fontWeight: '900',
    lineHeight: 42,
  },
  sectionTitleCompact: {
    fontSize: 27,
    lineHeight: 34,
    maxWidth: '100%',
  },
  stackBand: {
    flexDirection: 'column',
  },
  stackedActions: {
    alignItems: 'stretch',
    flexDirection: 'column',
    width: '100%',
  },
  textLinkButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  textLinkButtonText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
  },
  transparencyBand: {
    alignItems: 'center',
    backgroundColor: colors.text,
    borderRadius: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xl,
    padding: spacing.xl,
  },
  calloutBandCompact: {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: spacing.lg,
    padding: spacing.lg,
  },
  transparencyBody: {
    color: 'rgba(255, 255, 255, 0.78)',
    fontSize: 16,
    lineHeight: 24,
  },
  transparencyButton: {
    backgroundColor: colors.surface,
    borderColor: colors.surface,
    minWidth: 170,
  },
  transparencyButtonFull: {
    backgroundColor: colors.surface,
    borderColor: colors.surface,
    minWidth: 170,
    width: '100%',
  },
  transparencyCopy: {
    flexBasis: 420,
    flexGrow: 1,
    gap: spacing.sm,
  },
  transparencyIcon: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 58,
    justifyContent: 'center',
    width: 58,
  },
  transparencyTitle: {
    color: colors.surface,
    fontSize: 24,
    fontWeight: '900',
    lineHeight: 31,
  },
  calloutTitleCompact: {
    fontSize: 21,
    lineHeight: 27,
  },
  trustBand: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    justifyContent: 'space-between',
    padding: spacing.xl,
  },
  trustItem: {
    alignItems: 'center',
    flexBasis: 260,
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.md,
    minWidth: 0,
  },
  trustText: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 21,
  },
});
